import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AudioLines, Bot, Film, FolderOpen, Image as ImageIcon, ImagePlus, MessageSquare, Mic, Music, Search, SlidersHorizontal, Upload,
} from 'lucide-react';

export type CanvasAction =
  | 'upload-image'
  | 'upload-video'
  | 'upload-audio'
  | 'gallery'
  | 'gen-image'
  | 'gen-video'
  | 'edit-video'
  | 'voice'
  | 'music'
  | 'sfx'
  | 'comment'
  | 'assistant';

type ActionDef = {
  id: CanvasAction;
  group: string;
  label: string;
  hint: string;
  /** With Shift, on the canvas. */
  key: string;
  icon: React.ComponentType<{ size?: number }>;
  keywords: string;
};

export const CANVAS_ACTIONS: ActionDef[] = [
  { id: 'upload-image', group: 'Carregar mídia', label: 'Imagem do computador', hint: 'JPG, PNG, WEBP', key: 'I', icon: ImagePlus, keywords: 'upload foto arquivo carregar' },
  { id: 'upload-video', group: 'Carregar mídia', label: 'Vídeo do computador', hint: 'MP4, MOV', key: 'V', icon: Upload, keywords: 'upload clipe arquivo carregar' },
  { id: 'upload-audio', group: 'Carregar mídia', label: 'Áudio do computador', hint: 'MP3, WAV · ligue num vídeo editado', key: 'A', icon: AudioLines, keywords: 'upload som trilha musica arquivo carregar' },
  { id: 'gallery', group: 'Carregar mídia', label: 'Da galeria', hint: 'imagens e vídeos já enviados', key: 'G', icon: FolderOpen, keywords: 'galeria biblioteca midia' },
  { id: 'gen-image', group: 'Gerar com IA', label: 'Gerar imagem', hint: 'a partir de um texto', key: 'N', icon: ImageIcon, keywords: 'criar imagem ia prompt texto foto' },
  { id: 'gen-video', group: 'Gerar com IA', label: 'Gerar vídeo', hint: 'modelo, prompt, início, fim, vídeo e áudio', key: 'F', icon: Film, keywords: 'criar video fluxo gerar clipe gerador no' },
  { id: 'assistant', group: 'Gerar com IA', label: 'Falar com o assistente', hint: 'abre o chat do agente', key: '/', icon: Bot, keywords: 'agente chat ia assistente pedir' },
  { id: 'voice', group: 'Locução e som', label: 'Locução', hint: 'voz a partir de um texto', key: 'L', icon: Mic, keywords: 'voz narracao texto fala locucao' },
  { id: 'music', group: 'Locução e som', label: 'Música de fundo', hint: 'trilha instrumental', key: 'M', icon: Music, keywords: 'trilha musica fundo' },
  { id: 'sfx', group: 'Locução e som', label: 'Efeito sonoro', hint: 'som ambiente ou efeito', key: 'S', icon: AudioLines, keywords: 'efeito som ambiente sfx' },
  { id: 'edit-video', group: 'Edição', label: 'Vídeo editado', hint: 'mixa sons, legendas, fade e cor', key: 'E', icon: SlidersHorizontal, keywords: 'editar edicao legenda fade cor mix modificador' },
  { id: 'comment', group: 'Anotação', label: 'Comentário', hint: 'uma caixa de texto no fluxo', key: 'C', icon: MessageSquare, keywords: 'comentario nota caixa texto anotacao post-it sticky' },
];

export function actionForKey(e: KeyboardEvent): CanvasAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.key === '/') return 'assistant';
  if (!e.shiftKey) return null;
  const k = e.key.toUpperCase();
  return CANVAS_ACTIONS.find((a) => a.key === k)?.id || null;
}

function fold(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

type Props = {
  x: number;
  y: number;
  /** Viewport size, to keep the menu on screen. */
  bounds: { w: number; h: number };
  onPick: (action: CanvasAction) => void;
  onClose: () => void;
};

const MENU_W = 300;
const MENU_MAX_H = 420;

export default function CanvasContextMenu({ x, y, bounds, onPick, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const items = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return CANVAS_ACTIONS;
    return CANVAS_ACTIONS.filter((a) => fold(`${a.label} ${a.hint} ${a.group} ${a.keywords}`).includes(q));
  }, [query]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-idx="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const left = Math.max(8, Math.min(x, bounds.w - MENU_W - 8));
  const top = Math.max(8, Math.min(y, bounds.h - Math.min(MENU_MAX_H, 60 + items.length * 44) - 8));

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (items.length ? (i + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (items.length ? (i - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (items[active]) onPick(items[active].id);
    }
  };

  let lastGroup = '';
  return (
    <div
      className="tb-ctx"
      style={{ left, top, width: MENU_W, maxHeight: MENU_MAX_H }}
      role="menu"
      aria-label="Inserir no quadro"
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
      onWheel={(e) => e.stopPropagation()}
    >
      <label className="tb-ctx-search">
        <Search size={13} />
        <input
          ref={inputRef}
          value={query}
          placeholder="Buscar: imagem, vídeo, comentário…"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label="Buscar ação"
        />
      </label>
      <div className="tb-ctx-list" ref={listRef}>
        {!items.length && <p className="tb-ctx-empty">Nada encontrado.</p>}
        {items.map((a, i) => {
          const header = a.group !== lastGroup ? a.group : '';
          lastGroup = a.group;
          const Icon = a.icon;
          return (
            <React.Fragment key={a.id}>
              {header && <div className="tb-ctx-group">{header}</div>}
              <button
                type="button"
                role="menuitem"
                data-idx={i}
                className={`tb-ctx-item${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => onPick(a.id)}
              >
                <span className="tb-ctx-icon"><Icon size={14} /></span>
                <span className="tb-ctx-text">
                  <strong>{a.label}</strong>
                  <small>{a.hint}</small>
                </span>
                <kbd>{a.key === '/' ? '/' : `⇧${a.key}`}</kbd>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}